import { captureException } from '@sentry/react';
import { sceneAction } from 'redux-store/scene/scene.store';
import store from 'redux-store/store';
import { defaultViewerCalculations } from 'utils/constants';
import { OutlineColor } from 'utils/constants/colors';
import { RENDER_CANVAS } from 'utils/constants/elements';
import delay from 'utils/delay';
import { isE2eOrUnitTest, isE2eTest, isKiosk, isProduction } from 'utils/platform';

import cameraSettings from './cameraSettings';
import type ProductPlacementUtil from './productPlacementUtil';
import { applyMaterialsManually, getRoomCenterPosition, getRoomViewerObjectIds } from './roomUtils';
import screenshotSettings from './screenshotSettings';
import {
  CameraFramingMode,
  CameraTransitionPreset,
  type Coroutine,
  create_viewer,
  DracoDecoderSource,
  type IAdvancedViewer,
  type IDirectionalLight,
  type IExperimentalViewer,
  IVector3,
  type IViewer,
  LightType
} from '@inter-ikea-betrakta/viewer';
import { smooth_step } from '@inter-ikea-betrakta/viewer/dist/index';
import { OrbitPlannerCamera } from '@inter-ikea-betrakta/viewer/dist/modules/cameras/OrbitPlannerCamera';
import { create_viewer_webgl_mock } from '@inter-ikea-betrakta/viewer/dist/WebGLMock';
import { InputGatherer } from '@inter-ikea-betrakta/viewer/dist/modules/InputGatherer';
import { type Core, type Viewer } from '@inter-ikea-core/core';
import { IPEXViewerCoreAdapter } from '@inter-ikea-core/ipex-betrakta-core-adapter';
import { IKvadratConfig, Kvadrat } from '@inter-ikea-kvadrat/kvadrat';
import { getLocale } from 'i18n/i18nUtils';
import { serviceSettings } from 'utils/constants/serviceSettings';
import { defaultRenderSettings } from './helpers/renderSettings';

let orbitCamera: OrbitPlannerCamera;
let orbitCameraScreenshot: OrbitPlannerCamera;
let ipexViewer: IViewer;
let directionalLight: IDirectionalLight;
let productPlacementUtil: ProductPlacementUtil | undefined;
let core: Core | undefined;

interface ICameraAnimationParams {
  duration: number;
  position: IVector3;
  camera: OrbitPlannerCamera;
}

export const getBetraktaCrashReport = () => {
  const viewer = ipexViewer as IViewer & IExperimentalViewer & IAdvancedViewer;

  if (!viewer) {
    return;
  }

  // Create report only for kiosks for now [SKYT-1547]
  const canCreateReport = isKiosk() && !isE2eTest();
  if (canCreateReport) {
    const memory_tracker = viewer.get_webgl_memory_tracker();
    const memory_tracker_report = !memory_tracker ? 'null' : memory_tracker.get_memory_usage_report();

    //@ts-expect-error
    const effects = viewer._engine._compiledEffects;

    let babylon_shader_cache_count = 0;
    let babylon_shader_cache_waste_bytes = 0;

    try {
      const keys = Object.keys(effects);
      babylon_shader_cache_count = 0;
      keys.forEach((key) => {
        const effect = effects[key];
        babylon_shader_cache_count++;
        babylon_shader_cache_waste_bytes += key.length;
        babylon_shader_cache_waste_bytes += effect._fragmentSourceCode.length;
        babylon_shader_cache_waste_bytes += effect._vertexSourceCode.length;
        babylon_shader_cache_waste_bytes += effect.fragmentSourceCodeBeforeMigration.length;
        babylon_shader_cache_waste_bytes += effect.vertexSourceCodeBeforeMigration.length;

        babylon_shader_cache_waste_bytes += effect._fragmentSourceCodeOverride.length;
        babylon_shader_cache_waste_bytes += effect._vertexSourceCodeOverride.length;
      });
    } catch {
    } finally {
      const report = {
        webgl_usage: memory_tracker_report,
        babylon_shader_cache_count: babylon_shader_cache_count,
        babylon_shader_cache_waste_bytes: babylon_shader_cache_waste_bytes
      };

      return report;
    }
  }
  return null;
};

// coroutine function
export function* rotateOnAngleCoroutine(cameraParams: ICameraAnimationParams, on_done: () => void): Coroutine {
  const { duration, position, camera } = cameraParams;
  const startPos = IVector3.copy(position);
  const endPos = cameraCoordinates().position;
  let elapsedTime = 0;
  const cameraZPos = IVector3.copy(camera.position).z;

  while (elapsedTime < duration) {
    const delta = yield;
    elapsedTime += delta.milliseconds;
    const clampedTime = Math.min(1, elapsedTime / duration);
    const easedTime = smooth_step(clampedTime);

    const newX = startPos.x + (endPos.x - startPos.x) * easedTime;
    const newY = startPos.y + (endPos.y - startPos.y) * easedTime;
    const newZ = cameraZPos + (startPos.z - cameraZPos) * easedTime;

    camera.look_at({ x: newX, y: newY, z: newZ }, cameraCoordinates().target);
  }

  on_done();
  return undefined;
}

export const getViewer = () => {
  if (!ipexViewer) {
    const canvas = document.getElementById(RENDER_CANVAS) as HTMLCanvasElement | null;

    if (!canvas) {
      captureException(new Error('No canvas was ready to assign viewer with.'));
      return undefined;
    }

    let dracoSource: DracoDecoderSource = DracoDecoderSource.UNIVERSAL;

    const isIkeaCom = window.location.host.startsWith('www.ikea.com') || window.location.host.startsWith('ikea.com');

    if (isIkeaCom && !isKiosk()) {
      dracoSource = DracoDecoderSource.IKEA_COM;
    }

    const isHeadless = isE2eOrUnitTest() && !isProduction();
    const headlessConfig = isHeadless
      ? {
          headless: true as const,
          webgl_mock: create_viewer_webgl_mock()
        }
      : {};
    ipexViewer = create_viewer(canvas, dracoSource, {
      ...headlessConfig,
      enable_webgl_memory_tracker: isKiosk() ? true : undefined
    });
  }

  return ipexViewer;
};

export const getScreenshotCamera = () => {
  // Always create a new camera for screenshots instead of caching
  // This ensures each screenshot gets a fresh camera with current coordinates
  const camera = new OrbitPlannerCamera(cameraCoordinates().position, cameraCoordinates().target, ipexViewer);
  camera.field_of_view_degrees = defaultViewerCalculations.fieldOfView;
  return camera;
};

export const getCamera = () => {
  if (!orbitCamera) {
    orbitCamera = new OrbitPlannerCamera(cameraCoordinates().position, cameraCoordinates().target, ipexViewer);
    orbitCamera.field_of_view_degrees = defaultViewerCalculations.fieldOfView;
    ipexViewer.replace_camera(orbitCamera, CameraTransitionPreset.NONE);
  }
  return orbitCamera;
};

export const restrictCameraMovement = () => {
  const activeCamera = getCamera();
  const camTarget = activeCamera.target;
  // Will lock camera to one position, so we do not allow panning
  activeCamera.set_pan_limits({ min: camTarget, max: camTarget });
  // Will lock camera zoom to not be too close nor too far away
  activeCamera.set_zoom_limits({
    near: defaultViewerCalculations.limitsMinMax.min,
    far: defaultViewerCalculations.limitsMinMax.max
  });
};

export const onResize = () => {
  const canvas = document.getElementById(RENDER_CANVAS) as HTMLCanvasElement | null;
  if (!canvas) {
    return;
  }

  const { width: canvasWidth, height: canvasHeight } = canvas.getBoundingClientRect();
  let renderWidth = canvasWidth;
  let renderHeight = canvasHeight;

  if (renderWidth < 1 || renderHeight < 1) {
    const isMobile = store.getState().userAgent.isMobile;
    if (isMobile) {
      renderWidth = screenshotSettings.params.size.mobile.x;
      renderHeight = screenshotSettings.params.size.mobile.y;
    } else {
      renderWidth = screenshotSettings.params.size.desktop.x;
      renderHeight = screenshotSettings.params.size.desktop.y;
    }
  }

  if (renderWidth < 1 || renderHeight < 1) {
    renderWidth = 1920;
    renderHeight = 1080;
  }

  ipexViewer.resize(renderWidth, renderHeight);
};

export const addAmbientOcclusion = (enabled: boolean) => {
  ipexViewer.set_temporal_ao_parameters({ enabled });
};

export const createViewerDefaultSetup = (coreInstance: Core, productPlacementUtilInstance: ProductPlacementUtil) => {
  core = coreInstance;
  productPlacementUtil = productPlacementUtilInstance;

  const canvas = document.getElementById(RENDER_CANVAS) as unknown as HTMLCanvasElement; // TS requires to first convert to unknown

  ipexViewer = getViewer() as IViewer;
  const inputGatherer = new InputGatherer(canvas, ipexViewer);
  ipexViewer.get_viewer_dispose_observable().add_once(() => {
    return inputGatherer.dispose();
  });
  getCamera();
  restrictCameraMovement();

  const coreViewer: Viewer = setupCoreViewerBridge(coreInstance);
  overrideInitialTabIndex();

  const kvadrat = setupKvadrat(coreInstance, canvas);

  ipexViewer.set_global_outline_color(OutlineColor.hover);
  ipexViewer.set_global_outline_width(0.3);
  return { ipexViewer, coreViewer, kvadrat };
};

const setupKvadrat = (core: Core, canvas: HTMLCanvasElement) => {

  const conf: IKvadratConfig = {
    core,
    renderer: ipexViewer,
    canvas,
    locale: getLocale(),
    dexfApiKey: serviceSettings.dexfApiKey,
    dexfAppName: serviceSettings.dexfApplicationId,
    measurementSystem: 'metric',
    lightingOptions: {
        environmentMapUrl: defaultRenderSettings.environmentMap.url,
        environmentMapIntensity: 1.8,
      },
  };

  const kvadrat = new Kvadrat(conf);

  // Apply materials after room creation. This is the intended approach with Kvadrat v14+
  // Materials must be applied manually after room geometry is created to ensure proper rendering
  applyMaterialsManually(kvadrat, defaultRenderSettings.roomMaterials);
  adjustEnvironmentMap();
  setupLight();
  addAmbientOcclusion(true);

  return kvadrat;
};

export const awaitVisibleModelsOnScene = async () => {
  if (!ipexViewer) {
    return false;
  }

  return ipexViewer.on_current_scene_ready().then(async () => {
    /**
     * Making sure all loading boxes are gone before confirming models are loaded
     * This is needed for the screenshotUtil to not risk capturing boxes when going straight to summary from url
     *
     * loadingBoxAnimationDuration is taken from here:
     * https://github.com/inter-ikea/CBF-RE-IPEX-VIEWER/blob/main/package/viewer/src/modules/ProxyLoader.ts#L61
     */
    const loadingBoxAnimationDuration = 300;
    const objectIds = ipexViewer.get_all_objects();
    const promises: Array<Promise<void>> = [];
    objectIds.forEach((id) => {
      promises.push(ipexViewer.object_ready(id));
    });
    await Promise.all(promises);
    await delay(loadingBoxAnimationDuration);
    onResize();
    store.dispatch(sceneAction.setHasAllSelectedModelsLoaded(true));
    return true;
  });
};

const setupCoreViewerBridge = (core: Core) => {
  const coreViewer: Viewer = new IPEXViewerCoreAdapter(ipexViewer, core.services.entityManager, core.services.sceneManager);
  core.setViewer(coreViewer);

  return coreViewer;
};

const overrideInitialTabIndex = () => {
  // Set tabIndex directly on the canvas element (set_canvas_tab_index was removed in viewer v12)
  // https://forum.babylonjs.com/t/canvas-tabindex-1/8052
  const canvas = document.getElementById(RENDER_CANVAS) as HTMLCanvasElement | null;
  if (canvas) {
    canvas.tabIndex = -1;
  }
};

const centeredRoomDepthTarget = () => {
  // Get the actual room center from Kvadrat room entities
  if (core) {
    const roomCenter = getRoomCenterPosition(core.services);
    if (roomCenter) {
      return roomCenter.z;
    }
  }

  // Fallback to default if no room exists yet
  return defaultViewerCalculations.targetXYZ.z;
};

export const cameraCoordinates = () => {
  return {
    initialPosition: new IVector3(
      defaultViewerCalculations.preIntroAnimationX,
      defaultViewerCalculations.positionXYZ.y,
      defaultViewerCalculations.positionXYZ.z
    ),
    position: new IVector3(defaultViewerCalculations.positionXYZ.x, defaultViewerCalculations.positionXYZ.y, defaultViewerCalculations.positionXYZ.z),
    target: new IVector3(defaultViewerCalculations.targetXYZ.x, defaultViewerCalculations.targetXYZ.y, centeredRoomDepthTarget())
  };
};

const cameraDirection = () => {
  const position = new IVector3(
    defaultViewerCalculations.positionXYZ.x,
    defaultViewerCalculations.positionXYZ.y,
    defaultViewerCalculations.positionXYZ.z
  );
  const target = new IVector3(defaultViewerCalculations.targetXYZ.x, defaultViewerCalculations.targetXYZ.y, centeredRoomDepthTarget());

  const subtractedPosTar = IVector3.subtract(target, position);
  const direction = IVector3.normalize(subtractedPosTar);
  return direction;
};

const frameObjects = (camera: OrbitPlannerCamera, direction: IVector3) => {
  const cameraTargets = core ? getRoomViewerObjectIds(core.services) : [];

  if (!cameraTargets.length) {
    return;
  }

  return camera.frame_async(cameraTargets, CameraFramingMode.BOUNDING_BOX, cameraSettings.aspectRatio, direction, cameraSettings.margin);
};

export const setCameraRelativeToRoom = async (direction?: IVector3, camera?: OrbitPlannerCamera, noAnimate?: boolean, dontAwaitModels?: boolean) => {
  if (!ipexViewer) {
    return;
  }

  onResize();

  if (!dontAwaitModels) {
    const {
      scene: { hasAllSelectedModelsLoaded }
    } = store.getState();

    const allModelsVisible = await awaitVisibleModelsOnScene();

    if (!hasAllSelectedModelsLoaded && allModelsVisible) {
      store.dispatch(sceneAction.setHasAllSelectedModelsLoaded(true));
    }
  }

  direction = direction || cameraDirection();

  const activeCamera = camera || getCamera();
  if (noAnimate) {
    await frameObjects(activeCamera, direction);
  } else {
    const tempCamera = activeCamera.clone();

    await frameObjects(tempCamera, direction);

    await new Promise<void>((resolve) => {
      ipexViewer.start_coroutine(
        rotateOnAngleCoroutine({ duration: 750, position: tempCamera.position, camera: getCamera() }, () => {
          return resolve();
        })
      );
    });

    tempCamera.dispose();
  }

  restrictCameraMovement();
};

/**
 * Need to push the camera and wait one tick to trigger Kvadrat system to hide wall thickness
*/
export const pushScreenshotCamera = async (orbitPlannerCamera: OrbitPlannerCamera) => {
  ipexViewer.push_camera(orbitPlannerCamera, CameraTransitionPreset.NONE);
  await waitOneAnimationFrame();
};

export const popCameraAfterScreenshot = async () => {
  ipexViewer.pop_camera(CameraTransitionPreset.NONE);
  // Has to wait one frame for some reason, otherwise some artifacts from kvadrat are still visible in the next screenshot
  await waitOneAnimationFrame();
};

const waitOneAnimationFrame = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(resolve);
  });

const setupLight = () => {
  directionalLight = ipexViewer.create_light(LightType.DIRECTIONAL_LIGHT);
  setLightPosition();
  directionalLight.intensity = 3;
  directionalLight.shadow.enable_shadows = true;
  directionalLight.shadow.custom_shadow_near = 1;
  directionalLight.shadow.custom_shadow_far = 25.0;
  directionalLight.shadow.shadow_resolution = 2048;
};

const setLightPosition = (direction?: IVector3) => {
  const xCoord = 5.8;
  const YCoord = 10;
  const defaultZCoord = 3;
  let zCoord;

  if (direction) {
    zCoord = direction.z > 0 ? -defaultZCoord : defaultZCoord;
  } else {
    zCoord = getCamera().position.z < 0 ? -defaultZCoord : defaultZCoord;
  }

  directionalLight.direction = IVector3.normalize(new IVector3(-xCoord, -YCoord, -zCoord));
};

export const setShadows = (status: boolean) => {
  directionalLight.shadow.enable_shadows = status;
};

const adjustEnvironmentMap = () => {
  const { environmentMap } = defaultRenderSettings;

  ipexViewer.set_environment_map_rotation_radians(environmentMap.rotation);
  ipexViewer.set_environment_map_local_mode(environmentMap.localMode);
  ipexViewer.set_environment_map_intensity(environmentMap.intensity);
};
